import { Model, Web3DViewer } from '@technology/web3d';
import type { GaussianSplattingPlugin } from '@technology/web3d-plugin-gaussiansplatting';
import { SingleCapabilityProviderBase } from '@trimble/web3d-providers';
import { installTrimbleTileFetch } from '../utils/trimble-tile-fetch';

export class GaussianSplatCapabilityProvider extends SingleCapabilityProviderBase<Model> {
  public idKey = 'modelId' as const;

  private readonly fetchUninstalls = new Map<string, () => void>();
  /** Loads whose model is not in items() yet, but the user already hid them. */
  private readonly cancelledLoadIds = new Set<string>();
  private isDisposed = false;
  private pluginPromise:
    | Promise<GaussianSplattingPlugin | undefined>
    | undefined;

  constructor(viewer: Web3DViewer) {
    super(viewer);
  }

  public async loadPly(options: {
    id: string;
    url: string;
    fitToView?: boolean;
  }): Promise<Model | null> {
    return this.loadWithPlugin(
      options.id,
      options.fitToView,
      'Gaussian splat already added',
      (plugin) =>
        plugin.load(options.url, {
          modelId: options.id,
          ...(options.fitToView !== undefined && {
            fitToView: options.fitToView,
          }),
        })
    );
  }

  public async loadTileset(options: {
    id: string;
    tilesetUrl: string;
    templateUrl: string;
    fitToView?: boolean;
  }): Promise<Model | null> {
    if (!this.canStartLoad(options.id)) {
      return this.getById(options.id) ?? null;
    }

    this.removeTileFetch(options.id);
    this.fetchUninstalls.set(
      options.id,
      installTrimbleTileFetch(options.tilesetUrl, options.templateUrl)
    );

    try {
      return await this.loadWithPlugin(
        options.id,
        options.fitToView,
        'Gaussian splat tileset already added',
        (plugin) =>
          plugin.loadTiles(options.tilesetUrl, {
            modelId: options.id,
            ...(options.fitToView !== undefined && {
              fitToView: options.fitToView,
            }),
          })
      );
    } finally {
      // Drop the signed-url patch when the tileset never made it into the viewer.
      if (this.getById(options.id) == null) {
        this.removeTileFetch(options.id);
      }
    }
  }

  public async unloadTileset(id: Model[typeof this.idKey]): Promise<void> {
    // plugin.load / loadTiles may still be in flight. items() does not
    // contain the model until finishLoad, so remember the cancel and drop
    // the model when that call returns.
    this.cancelledLoadIds.add(id);
    const model = this.getById(id);

    if (model != null) {
      await this.viewer.unload(model.modelId, { fitToView: false });
      this.removeItem(model);
    }

    this.removeTileFetch(id);
    if (!this.isItemLoading(id)) {
      this.cancelledLoadIds.delete(id);
    }
  }

  public async cleanup(): Promise<void> {
    this.isDisposed = true;
    const ids = new Set<string>([
      ...this.items().map((model) => model[this.idKey]),
      ...this.fetchUninstalls.keys(),
    ]);
    await Promise.all([...ids].map((id) => this.unloadTileset(id)));
    this.clearAllTileFetches();
  }

  private canStartLoad(id: string): boolean {
    return (
      !this.isDisposed && this.getById(id) == null && !this.isItemLoading(id)
    );
  }

  /**
   * Shared ply and tileset load. `plugin.load` / `plugin.loadTiles` are the
   * calls that throw from the gaussian plugin and 3D Tiles renderer. Those
   * rejections propagate so the data-loading handler can mark the item failed
   * and show the user the Gaussian splat load error.
   */
  private async loadWithPlugin(
    id: string,
    fitToView: boolean | undefined,
    duplicateError: string,
    load: (plugin: GaussianSplattingPlugin) => Promise<Model>
  ): Promise<Model | null> {
    if (!this.canStartLoad(id)) {
      return this.getById(id) ?? null;
    }

    this.addToLoadingSet(id);
    try {
      const plugin = await this.getPlugin();
      if (plugin == null || this.wasLoadCancelled(id)) {
        return null;
      }

      const model = await load(plugin);
      return await this.finishLoad(id, model, fitToView, duplicateError);
    } finally {
      this.removeFromLoadingSet(id);
      this.cancelledLoadIds.delete(id);
    }
  }

  private getPlugin(): Promise<GaussianSplattingPlugin | undefined> {
    this.pluginPromise ??= this.attachPlugin().catch((error: unknown) => {
      this.pluginPromise = undefined;
      throw error;
    });
    return this.pluginPromise;
  }

  private async attachPlugin(): Promise<GaussianSplattingPlugin | undefined> {
    const { GaussianSplattingPlugin } = await import(
      '@technology/web3d-plugin-gaussiansplatting'
    );
    const existing = this.viewer.plugins.get(GaussianSplattingPlugin);
    if (existing != null) {
      return existing;
    }

    if (this.isDisposed) {
      return undefined;
    }

    const plugin = new GaussianSplattingPlugin();
    this.viewer.addPlugin(plugin);
    return plugin;
  }

  private wasLoadCancelled(id: string): boolean {
    return this.isDisposed || this.cancelledLoadIds.has(id);
  }

  private async finishLoad(
    id: string,
    model: Model,
    fitToView: boolean | undefined,
    duplicateError: string
  ): Promise<Model | null> {
    if (this.wasLoadCancelled(id)) {
      await this.viewer.unload(model.modelId, { fitToView: false });
      return null;
    }

    if (!this.addItem(model)) {
      throw new Error(duplicateError);
    }

    await this.viewer.waitForRenderIdle();

    if (fitToView !== false) {
      await this.viewer.camera.fitToView();
    }

    return model;
  }

  private removeTileFetch(id: string): void {
    this.fetchUninstalls.get(id)?.();
    this.fetchUninstalls.delete(id);
  }

  private clearAllTileFetches(): void {
    for (const uninstall of this.fetchUninstalls.values()) {
      uninstall();
    }
    this.fetchUninstalls.clear();
  }
}
